import { Button } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import { Form } from 'components/organisms';
import { ContractTabs } from 'components/organisms/forms/contract/ContractForm';
import { FormikHelpers } from 'formik';
import { useContractParamIds, useHasPermission } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import { t } from 'i18next';
import middleware from 'middleware';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiArrowsClockwise,
  PiFilePdf,
  PiPencilSimpleLine,
} from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useBulkUpdateContract,
  useDeleteContract,
  useGetContract,
  useGetContractsByLabel,
} from 'services/contract';
import { useCreateContractPDF } from 'services/contract/pdf';
import { CONTRACT_STATUS, IContractBulkUpdate } from 'services/contract/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { contractTemplateUtil } from 'utils';
import { formatDateToInput } from 'utils/global/date.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { object, string } from 'yup';

const contractBulkUpdateInitialValues: IContractBulkUpdate = {
  currentContractId: '',
  newContractId: '',
  startDate: formatDateToInput(new Date()),
};
const contractBulkUpdatelValidator = object().shape({
  newContractId: string().required(() => t('formik-error.required')),
  startDate: string().required(() => t('formik-error.required')),
});

const Contract = () => {
  const labelId = useParamId('/label');
  const contractId = useContractParamIds(`/label/${labelId}/contract`);
  const hasPermission = useHasPermission();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const createPDF = useCreateContractPDF();

  const removeContract = useDeleteContract();

  const bulkUpdate = useBulkUpdateContract();

  const contract = useGetContract(contractId, {
    notFoundPath: `/label/${labelId}/contract`,
  });

  const finalBackPath =
    location.state === 'contracts'
      ? 'contracts'
      : location.state === 'contracts-to-expire'
      ? 'contracts-to-expire'
      : `label/${labelId}/contract`;

  const onRemoveContract = async () => {
    await removeContract.mutateAsync(contractId, {
      onSuccess: () => navigate(`/label/${labelId}/contract`),
    });
  };

  useEffect(() => {
    if (createPDF.data && contract.data) {
      const url = window.URL.createObjectURL(createPDF.data);
      const a = document.createElement('a');
      a.href = url;
      a.download =
        `${contract.data.labelName} ${contract.data.title}.pdf`.replaceAll(
          ' ',
          '_'
        );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      createPDF.reset();
    }
  }, [createPDF.data]);

  const labelContracts = useGetContractsByLabel(labelId, {
    params: { itemCount: 999, page: 0, ShowOnlyActive: false },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const contractOptions =
    labelContracts.data?.pages
      .flat()
      .filter(labelContract => labelContract.id !== contractId)
      .map(contract => ({ value: contract.id, label: contract.title })) ?? [];

  useEffect(() => {
    labelContracts.refetch();
  }, [labelId]);

  const onSubmitBulkUpdate = async (
    values: IContractBulkUpdate,
    formikHelpers: FormikHelpers<IContractBulkUpdate>,
    actions: PopupActions | null
  ) =>
    await bulkUpdate.mutateAsync(
      {
        ...values,
        currentContractId: contractId,
      },
      {
        onError: error => onErrorShowAllErrorsToast(error),
        onSuccess: () =>
          navigate(`/label/${labelId}/contract/${values.newContractId}/form`),
      }
    );

  return (
    <Form.Contract
      backPath={finalBackPath}
      topbarContent={
        <div className='flex'>
          {contract.data?.status === CONTRACT_STATUS.active && (
            <MagellanPopups.Form
              initialValues={contractBulkUpdateInitialValues}
              validationSchema={contractBulkUpdatelValidator}
              onSubmit={onSubmitBulkUpdate}
              header={t('contract-template.bulk-assign.header')}
              submitButton={
                <Button type='submit' variant={BUTTON_VARIANT.submit}>
                  <Button.Text text={t('button.change')} />
                </Button>
              }
              trigger={
                <Button isClear onlyIcon>
                  <Button.Icon icon={<PiArrowsClockwise />} />
                  <Button.Text text={t('button.replace')} />
                </Button>
              }>
              {({ values }) => (
                <>
                  <FormInput.Select
                    name='newContractId'
                    label={t('input.label.new-contract')}
                    options={contractOptions}
                    isAsync
                  />
                  <FormInput.Text
                    label={t('input.label.startDate')}
                    type='date'
                    name='startDate'
                  />
                </>
              )}
            </MagellanPopups.Form>
          )}
          <Button
            isClear
            onlyIcon
            disabled={!contract.data}
            onClick={async () =>
              await createPDF.mutateAsync(contractId, {
                onSuccess: data => console.log(data),
              })
            }>
            <Button.Icon icon={<PiFilePdf />} />
          </Button>

          {hasPermission('ContractPermissionsLIST**', 2) && (
            <Button
              isClear
              onlyIcon
              onClick={() =>
                navigate(`/label/${labelId}/contract/edit/${contractId}/form`, {
                  replace: true,
                })
              }>
              <Button.Icon icon={<PiPencilSimpleLine />} />
            </Button>
          )}
          {hasPermission('ContractPermissionsLIST**', 4) &&
            contract.data?.status === CONTRACT_STATUS.offer && (
              <MagellanPopups.DeleteWarning
                onRemove={onRemoveContract}
                removeLoading={removeContract.isPending}
                name={contract.data?.title}
              />
            )}
        </div>
      }
      withTabs
      disabledTabs={
        contract.data?.status === CONTRACT_STATUS.active
          ? []
          : [ContractTabs.products, ContractTabs.tracks, ContractTabs.videos]
      }
      initialStatus={{
        ...contractTemplateUtil.contract.initialStatus,
        displayMode: true,
      }}
      initialValues={middleware.contract.queryToForm(contract.data)}
      onSubmit={() => {}}
    />
  );
};

export default Contract;
